import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore,
  Download,
  ArrowForward,
} from '@mui/icons-material';
import { ProjectDefinition } from '../../types';

interface ProjectDefinitionViewerProps {
  project: ProjectDefinition;
  onExport: () => void;
  onClose: () => void;
}

const ProjectDefinitionViewer: React.FC<ProjectDefinitionViewerProps> = ({
  project,
  onExport,
  onClose,
}) => {
  const handleExportJSON = () => {
    const jsonString = JSON.stringify(project, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = `${project.projectName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    onExport();
  };

  const getPhaseName = (phaseId: number): string => {
    const phase = project.phases.find(p => p.phaseId === phaseId);
    return phase ? phase.name : phaseId.toString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {project.projectName}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportJSON}
          >
            Export JSON
          </Button>
          <Button
            variant="contained"
            onClick={onClose}
          >
            Close
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Project Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="body2">
            <strong>Project ID:</strong> {project.projectId}
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong> {new Date(project.createdDate).toLocaleString()}
          </Typography>
          <Typography variant="body2">
            <strong>Last Updated:</strong> {new Date(project.lastUpdated).toLocaleString()}
          </Typography>
          <Typography variant="body2">
            <strong>Total Phases:</strong> {project.phases.length}
          </Typography>
          <Typography variant="body2">
            <strong>Total Transitions:</strong> {project.transitions.length}
          </Typography>
        </Box>
      </Paper>

      {/* Phases Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Phases ({project.phases.length})
        </Typography>
        <List>
          {project.phases.map((phase, index) => (
            <Accordion key={phase.phaseId} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mr: 2 }}>
                    {index + 1}. {phase.name}
                  </Typography>
                  {phase.exitActions.length > 0 && (
                    <Chip
                      label={`${phase.exitActions.length} exit action${phase.exitActions.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ pl: 2 }}>
                  {phase.description && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Description:</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {phase.description}
                      </Typography>
                    </Box>
                  )}
                  
                  {phase.entryCriteria && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Entry Criteria:</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {phase.entryCriteria}
                      </Typography>
                    </Box>
                  )}
                  
                  {phase.exitActions.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Exit Actions:</strong>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {phase.exitActions.map((action, idx) => (
                          <Chip
                            key={idx}
                            label={action}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                  
                  {!phase.description && !phase.entryCriteria && phase.exitActions.length === 0 && (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      No additional details configured
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </List>
      </Paper>

      {/* Transitions Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Transitions ({project.transitions.length})
        </Typography>
        {project.transitions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No transitions defined
          </Typography>
        ) : (
          <List>
            {project.transitions.map((transition, index) => (
              <React.Fragment key={transition.transitionId}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip 
                          label={getPhaseName(transition.fromPhaseId)} 
                          size="small" 
                          color="default"
                        />
                        <ArrowForward fontSize="small" color="action" />
                        <Chip 
                          label={getPhaseName(transition.toPhaseId)} 
                          size="small" 
                          color="primary"
                        />
                        {transition.condition && (
                          <Chip
                            label={transition.condition}
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      transition.condition ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          Condition: {transition.condition}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          No condition specified
                        </Typography>
                      )
                    }
                  />
                </ListItem>
                {index < project.transitions.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default ProjectDefinitionViewer;

